import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, MapPin, Calendar, Building2, Package } from "lucide-react";
import { LogoFull } from "../components/Logo";
import { Lightbox } from "../components/Lightbox";
import { periodLabel } from "../components/LavoriRealizzati";
import { splitParagraphs } from "../lib/products";
import { productSheetHref } from "../lib/projectOptions";
import { useSeo } from "../lib/seo";

const API = process.env.REACT_APP_BACKEND_URL;
const SITE = (process.env.REACT_APP_SITE_URL || "").replace(/\/$/, "");

const Info = ({ icon: Icon, label, value }) => value ? (
    <div className="rounded-2xl bg-slate-50 px-4 py-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#4F5D70]">{label}</div>
        <div className="mt-1 flex items-center gap-1.5 text-sm font-bold text-[#0B1F3A]"><Icon className="h-4 w-4 text-[#E30613]" />{value}</div>
    </div>
) : null;

// Pagina pubblica di un lavoro: /realizzazioni/<slug>. Titolo, descrizione, canonical, OG e JSON-LD impostati dalla pagina.
export default function ProjectPage() {
    const { slug } = useParams();
    const [p, setP] = useState(null);
    const [missing, setMissing] = useState(false);
    const [lb, setLb] = useState(null);

    useEffect(() => {
        axios.get(`${API}/api/realizzazioni/${slug}`).then((r) => setP(r.data)).catch(() => setMissing(true));
    }, [slug]);

    const title = p ? `${p.title} — Realizzazioni FTM Group S.r.l.` : "Realizzazioni | FTM Group S.r.l.";
    const description = p ? (p.short || `${p.division} a ${p.city}${p.province ? ` (${p.province})` : ""}: lavoro realizzato da FTM Group S.r.l.`).slice(0, 160) : "";
    useSeo({ title, description, path: `/realizzazioni/${slug}`, image: p?.cover || "/og-image.jpg", noindex: missing });

    useEffect(() => {
        if (!p) return undefined;
        const el = document.createElement("script");
        el.type = "application/ld+json";
        el.dataset.testid = "project-jsonld";
        el.text = JSON.stringify({ "@context": "https://schema.org", "@type": "Article", headline: p.title, description, datePublished: p.published_at, dateModified: p.updated_at, image: p.photos.map((f) => `${SITE}${f.url}`), author: { "@type": "Organization", name: "FTM Group S.r.l.", "@id": `${SITE}/#organization` }, publisher: { "@id": `${SITE}/#organization` }, mainEntityOfPage: `${SITE}/realizzazioni/${p.slug}`, about: p.division, contentLocation: { "@type": "Place", name: [p.city, p.province].filter(Boolean).join(" ") } });
        document.head.appendChild(el);
        return () => el.remove();
    }, [p, description]);

    const images = (p?.photos || []).map((f) => ({ src: `${API}${f.url}`, label: p.title }));

    return (
        <div className="min-h-screen bg-white" data-testid="project-page">
            <header className="border-b border-slate-200 bg-white">
                <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-4 sm:px-8">
                    <Link to="/" aria-label="FTM Group — home"><LogoFull /></Link>
                    <a href="/#realizzazioni" className="inline-flex items-center gap-2 text-sm font-bold text-[#0B1F3A] hover:text-[#E30613]" data-testid="project-back"><ArrowLeft className="h-4 w-4" /> Tutte le realizzazioni</a>
                </div>
            </header>
            <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8 lg:py-16">
                {missing && (
                    <div className="py-20 text-center" data-testid="project-missing">
                        <h1 className="font-display text-3xl font-extrabold text-[#0B1F3A]">Lavoro non trovato</h1>
                        <p className="mt-3 text-sm text-[#4F5D70]">Il lavoro che cerchi non è disponibile o non è più pubblicato.</p>
                        <a href="/#realizzazioni" className="mt-6 inline-block rounded-full bg-[#E30613] px-6 py-3 text-sm font-bold text-white">Torna alle realizzazioni</a>
                    </div>
                )}
                {p && (
                    <article>
                        <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#E30613]" data-testid="project-division">{p.division}</span>
                        <h1 className="font-display mt-3 text-3xl font-extrabold tracking-tight text-[#0B1F3A] sm:text-4xl lg:text-5xl" data-testid="project-title">{p.title}</h1>
                        {p.short && <p className="mt-5 max-w-3xl text-base leading-relaxed text-[#4F5D70] sm:text-lg" data-testid="project-short">{p.short}</p>}
                        <div className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="project-info">
                            <Info icon={MapPin} label="Luogo" value={[p.city, p.province && `(${p.province})`].filter(Boolean).join(" ")} />
                            <Info icon={Calendar} label="Periodo" value={periodLabel(p.period)} />
                            <Info icon={Building2} label="Tipo di cliente" value={p.client_type} />
                        </div>
                        {(p.products || []).length > 0 && (
                            <div className="mt-6" data-testid="project-products">
                                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4F5D70]">Prodotti installati</div>
                                <ul className="mt-2 flex flex-wrap gap-2">
                                    {p.products.map((r) => (
                                        <li key={r.key}>
                                            <a href={productSheetHref(r)} data-testid={`project-product-link-${r.key}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-bold text-[#0B1F3A] transition-colors hover:border-[#E30613]/50 hover:bg-white">
                                                <Package className="h-4 w-4 text-[#E30613]" />{r.name}<span className="text-xs font-normal text-[#4F5D70]">· scheda prodotto</span>
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="project-gallery">
                            {p.photos.map((f, i) => (
                                <button key={f.id} type="button" onClick={() => setLb(i)} data-testid={`project-photo-${i}`} className={`group overflow-hidden rounded-2xl bg-slate-100 ${i === 0 ? "sm:col-span-2 lg:col-span-2 lg:row-span-2" : ""}`} aria-label={`Foto ${i + 1}`}>
                                    <img src={`${API}${f.url}`} alt={`${p.title} — foto ${i + 1}`} loading={i === 0 ? "eager" : "lazy"} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                </button>
                            ))}
                        </div>
                        {p.description && (
                            <div className="mt-10 max-w-3xl space-y-4" data-testid="project-description">
                                {splitParagraphs(p.description).map((par) => <p key={par.slice(0, 40)} className="text-base leading-relaxed text-[#334155]">{par}</p>)}
                            </div>
                        )}
                        <div className="mt-12 rounded-3xl bg-[#0B1F3A] p-8 text-white sm:p-10">
                            <h2 className="font-display text-2xl font-extrabold">Hai un progetto simile?</h2>
                            <p className="mt-2 max-w-xl text-sm text-white/75">Sopralluogo e preventivo sono gratuiti. Raccontaci la tua esigenza: ti ricontattiamo nel più breve tempo possibile.</p>
                            <a href={`/?divisione=${encodeURIComponent(p.division)}#contatti`} data-testid="project-quote-button" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#E30613] px-6 py-3.5 text-sm font-bold text-white hover:bg-[#C20410]">Richiedi un preventivo simile <ArrowRight className="h-4 w-4" /></a>
                        </div>
                        <p className="mt-6 text-xs text-[#94A3B8]">Foto pubblicate con il consenso del cliente; targhe e volti sono stati oscurati a tutela della privacy.</p>
                    </article>
                )}
            </main>
            {lb !== null && <Lightbox items={images} index={lb} onClose={() => setLb(null)} onChange={setLb} />}
        </div>
    );
}
